import { IdentityLink } from "@/components/identity-link";
import { AnyField } from "@/components/moon/any-field";
import { InputField } from "@/components/moon/input-field";
import { ResourceListPage } from "@/components/page-templates";
import { DirectoryPeopleSummary } from "@/pages/identities/DirectoryPeopleSummary";
import { Dialog } from "@/components/ui/Dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { Text } from "@/components/ui/Text";
import { useOrganization, useUser } from "@/contexts/Auth";
import { HumanizeDateTime } from "@/lib/dates";
import { formatDistanceToNow } from "date-fns";
import { z } from "zod";
import {
  invalidateAllListInvites,
  useListInvitesSuspense,
} from "@gram/client/react-query/listInvites.js";
import {
  invalidateAllListOrganizationUsers,
  useListOrganizationUsersSuspense,
} from "@gram/client/react-query/listOrganizationUsers.js";
import { useRemoveOrganizationUserMutation } from "@gram/client/react-query/removeOrganizationUser.js";
import { useRevokeInviteMutation } from "@gram/client/react-query/revokeInvite.js";
import { useSendInviteMutation } from "@gram/client/react-query/sendInvite.js";
import { useUpdateInviteRoleMutation } from "@gram/client/react-query/updateInviteRole.js";
import { useMembers } from "@gram/client/react-query/members.js";
import { useRoles } from "@gram/client/react-query/roles.js";
import { OrganizationInvitation } from "@gram/client/models/components/organizationinvitation.js";
import { OrganizationUser } from "@gram/client/models/components/organizationuser.js";
import { SimpleTooltip } from "@/components/ui/Tooltip";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { Icon } from "@/components/ui/Icon";
import { Input } from "@/components/ui/Input";
import { Stack } from "@/components/ui/Stack";
import { Column, Table } from "@/components/ui/Table";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateAllDirectoryPeople } from "@gram/client/react-query/directoryPeople.js";
import { directoryPeopleQueryKey } from "@/pages/identities/directoryPeople";
import {
  ChevronLeft,
  ChevronRight,
  Ellipsis,
  FolderSync,
  RefreshCw,
  Shield,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { RequireScope } from "@/components/require-scope";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/ContextMenu";
import { useOrgRoutes } from "@/routes";
import { cn } from "@/lib/utils";
import { getIdentityTint, useIsDarkTheme } from "@/components/gradient-colors";
import type { AccessMember } from "@gram/client/models/components/accessmember.js";
import { ChangeRoleDialog } from "@/pages/access/ChangeRoleDialog";
import { killswitchCreateHref } from "@/components/killswitch/killswitch-routing";
import { useKillswitchAccess } from "@/hooks/useKillswitchAccess";
import { useIdentityHrefBuilder } from "@/lib/useIdentityHref";
import {
  allMemberRoleIds,
  describeMemberRoleSource,
  memberRoleSource,
  shortMemberRoleSource,
} from "@/lib/member-roles";

/**
 * Everything from TeamInner's scope that the member actions menu needs,
 * so the dropdown render fn and MemberRowContextMenu can build the same
 * menu model for a member.
 */
/**
 * A role chip on a member's row. A role granted through a directory role
 * mapping names where it comes from, including when it is also assigned
 * directly, because the directory part cannot be removed from the member here.
 */
function MemberRoleLink({
  href,
  name,
  sourceDescription,
}: {
  href: string;
  name: string;
  /** Undefined for a role that is only assigned directly. */
  sourceDescription: string | undefined;
}): JSX.Element {
  const link = (
    <Link
      to={href}
      className="text-foreground hover:text-primary border px-1.5 py-0.5 text-xs no-underline transition-colors"
    >
      {name}
    </Link>
  );
  if (!sourceDescription) return link;
  return <SimpleTooltip tooltip={sourceDescription}>{link}</SimpleTooltip>;
}

/** A role name in the Team page's overflow tooltip, noting where it comes from. */
function overflowRoleLabel(name: string, source: string | undefined): string {
  if (!source) return name;
  return `${name} (${source})`;
}

type MemberMenuDeps = {
  accessMemberByUserId: Map<string, AccessMember>;
  adminCount: number;
  adminRoleId: string | undefined;
  challengesHref: string;
  /** This member's access tab, where their killswitches are managed. */
  identityAccessHref: (userId: string) => string | null;
  canUseKillswitch: boolean;
  navigate: ReturnType<typeof useNavigate>;
  roleIdsByUserId: Map<string, string[]>;
  scimManaged: boolean;
  setChangingMember: (member: AccessMember | null) => void;
  setMemberToRemove: (member: OrganizationUser | null) => void;
  userEmail: string;
};

type MemberMenuModel = {
  accessMember: AccessMember | undefined;
  canRemove: boolean;
  openChallenges: () => void;
  openKillswitch: () => void;
  openManageRoles: () => void;
  openViewKillswitches: () => void;
  openRemove: () => void;
  scimManaged: boolean;
  showChallenges: boolean;
  showKillswitch: boolean;
  showManageRoles: boolean;
};

/**
 * Single source of truth for the per-member actions menu: the visible "⋯"
 * dropdown in the actions column and the row's right-click context menu both
 * render from this model, so their conditions and callbacks stay in sync.
 */
function getMemberMenuModel(
  member: OrganizationUser,
  deps: MemberMenuDeps,
): MemberMenuModel {
  const memberRoleIds = deps.roleIdsByUserId.get(member.userId) ?? [];
  const identityAccessHref = deps.identityAccessHref(member.userId);
  const isLastAdmin =
    deps.adminRoleId != null &&
    memberRoleIds.includes(deps.adminRoleId) &&
    deps.adminCount <= 1;
  const isSelf = member.email === deps.userEmail;
  const accessMember: AccessMember | undefined =
    deps.accessMemberByUserId.get(member.userId) ??
    (memberRoleIds.length > 0
      ? {
          id: member.userId,
          principalUrn: `user:${member.userId}`,
          name: member.name,
          email: member.email,
          photoUrl: member.photoUrl,
          roleIds: memberRoleIds,
          directoryRoleIds: [],
          joinedAt: member.createdAt,
        }
      : undefined);

  return {
    accessMember,
    canRemove: !isSelf && !isLastAdmin,
    openChallenges: () => {
      void setTimeout(() => {
        void deps.navigate(
          `${deps.challengesHref}?identity=${encodeURIComponent(member.email)}`,
        );
      }, 0);
    },
    openKillswitch: () => {
      if (identityAccessHref) {
        void deps.navigate(killswitchCreateHref(identityAccessHref));
      }
    },
    openViewKillswitches: () => {
      if (identityAccessHref) void deps.navigate(identityAccessHref);
    },
    openManageRoles: () => {
      if (!accessMember) return;
      void setTimeout(() => deps.setChangingMember(accessMember), 0);
    },
    openRemove: () => {
      void setTimeout(() => deps.setMemberToRemove(member), 0);
    },
    scimManaged: deps.scimManaged,
    showChallenges: true,
    showKillswitch: deps.canUseKillswitch && identityAccessHref !== null,
    showManageRoles: true,
  };
}

/**
 * Right-click menu for a member row, mirroring the actions-column dropdown
 * item for item (same model, same permission gating). Renders children
 * unwrapped when no menu item is renderable.
 */
function MemberRowContextMenu({
  member,
  deps,
  children,
}: {
  member: OrganizationUser;
  deps: MemberMenuDeps;
  children: React.ReactElement;
}): React.JSX.Element {
  const model = getMemberMenuModel(member, deps);
  const identityHref = useIdentityHrefBuilder();
  const profileHref = identityHref({ userId: member.userId });
  const hasManageRoles =
    model.showManageRoles && (model.scimManaged || model.accessMember != null);
  const hasItemsAbove =
    hasManageRoles || model.showChallenges || model.showKillswitch;

  if (!profileHref && !hasItemsAbove && !model.canRemove) {
    return <>{children}</>;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent className="min-w-[10rem]">
        {profileHref && (
          <ContextMenuItem asChild>
            <Link to={profileHref}>View profile</Link>
          </ContextMenuItem>
        )}
        {model.showManageRoles &&
          (model.scimManaged ? (
            <ContextMenuItem disabled>Manage roles</ContextMenuItem>
          ) : (
            model.accessMember && (
              <RequireScope scope="org:admin" level="component">
                <ContextMenuItem onSelect={model.openManageRoles}>
                  Manage roles
                </ContextMenuItem>
              </RequireScope>
            )
          ))}
        {model.showChallenges && (
          <ContextMenuItem onSelect={model.openChallenges}>
            View challenges
          </ContextMenuItem>
        )}
        {model.showKillswitch && (
          <>
            <ContextMenuItem onSelect={model.openViewKillswitches}>
              View killswitches
            </ContextMenuItem>
            <ContextMenuItem onSelect={model.openKillswitch}>
              New killswitch…
            </ContextMenuItem>
          </>
        )}
        {model.canRemove && (
          <>
            <ContextMenuSeparator />
            <RequireScope scope="org:admin" level="component">
              <ContextMenuItem
                variant="destructive"
                onSelect={model.openRemove}
              >
                Remove member
              </ContextMenuItem>
            </RequireScope>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}

export default function Team(): JSX.Element {
  return (
    <RequireScope scope="org:admin" level="page">
      <TeamInner />
    </RequireScope>
  );
}

function TeamInner() {
  const isDark = useIsDarkTheme();
  const organization = useOrganization();
  const user = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const identityHref = useIdentityHrefBuilder();
  const orgRoutes = useOrgRoutes();

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteEmailTouched, setInviteEmailTouched] = useState(false);
  const [inviteRoleId, setInviteRoleId] = useState<string | undefined>(
    undefined,
  );

  const [memberToRemove, setMemberToRemove] = useState<OrganizationUser | null>(
    null,
  );
  const [inviteToCancel, setInviteToCancel] =
    useState<OrganizationInvitation | null>(null);
  const [changingMember, setChangingMember] = useState<AccessMember | null>(
    null,
  );

  const MEMBERS_PAGE_SIZE = 10;
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");

  const { data: membersData } = useListOrganizationUsersSuspense();
  const { data: invitesData } = useListInvitesSuspense();
  const { data: rolesData } = useRoles();
  const { data: accessMembersData } = useMembers();

  const allMembers = useMemo(
    () =>
      [...(membersData?.users ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    [membersData?.users],
  );
  const members = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allMembers;
    return allMembers.filter(
      (m) =>
        m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q),
    );
  }, [allMembers, search]);
  const totalPages = Math.ceil(members.length / MEMBERS_PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages - 1, 0));
  const visibleMembers = members.slice(
    safePage * MEMBERS_PAGE_SIZE,
    (safePage + 1) * MEMBERS_PAGE_SIZE,
  );
  // Killswitches are managed on the person's own access tab, so every entry
  // point here — both menu items — goes to the same page.
  const identityAccessHref = useIdentityHrefBuilder("access");
  const memberAccessHref = (userId: string) => identityAccessHref({ userId });
  const killswitchAccess = useKillswitchAccess();
  const invites = invitesData?.invitations ?? [];
  const roles = rolesData?.roles ?? [];
  const accessMembers = accessMembersData?.members ?? [];
  const memberRole = roles.find(
    (r) => r.slug === "member" || r.name.toLowerCase() === "member",
  );
  const memberRoleId = memberRole?.id;
  const defaultRoleId = memberRoleId;
  const effectiveInviteRoleId = inviteRoleId ?? defaultRoleId;

  // Cross-reference AccessMember (has roleIds) by user ID
  const roleIdsByUserId = new Map(accessMembers.map((m) => [m.id, m.roleIds]));
  const accessMemberByUserId = new Map(accessMembers.map((m) => [m.id, m]));
  const roleBySlug = new Map(roles.map((role) => [role.slug, role]));
  const roleById = new Map(roles.map((role) => [role.id, role]));
  const getRoleName = (roleId: string) =>
    roleById.get(roleId)?.name ?? "Unknown";
  const getInviteRole = (invite: OrganizationInvitation) =>
    invite.roleSlug ? roleBySlug.get(invite.roleSlug) : undefined;
  const getInviteRoleId = (invite: OrganizationInvitation) =>
    getInviteRole(invite)?.id;

  // Identify admin role and count admins for last-admin protection
  const adminRoleId = roles.find((r) => r.name.toLowerCase() === "admin")?.id;
  const adminCount = adminRoleId
    ? accessMembers.filter((m) => m.roleIds.includes(adminRoleId)).length
    : 0;

  const invalidateTeamData = async () => {
    await Promise.all([
      invalidateAllListOrganizationUsers(queryClient),
      invalidateAllListInvites(queryClient),
    ]);
  };
  // Removing a member moves their directory profile out of "member".
  // Invitations change no directory state, so only removal does this.
  const invalidateAfterRemoval = async () => {
    await Promise.all([
      invalidateTeamData(),
      invalidateAllDirectoryPeople(queryClient),
      queryClient.invalidateQueries({
        queryKey: directoryPeopleQueryKey(organization.id),
      }),
    ]);
  };

  const emailSchema = z.string().email();
  const inviteEmailError =
    inviteEmailTouched &&
    inviteEmail.trim() !== "" &&
    !emailSchema.safeParse(inviteEmail.trim()).success
      ? "Please enter a valid email address"
      : undefined;

  const inviteMutation = useSendInviteMutation({
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to send invite",
      );
    },
  });

  const removeMemberMutation = useRemoveOrganizationUserMutation({
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Failed to remove member",
      );
    },
  });

  const revokeInviteMutation = useRevokeInviteMutation({
    onError: () => {
      toast.error("Failed to cancel invite");
    },
  });

  const updateInviteRoleMutation = useUpdateInviteRoleMutation({
    onError: () => {
      toast.error("Failed to update invite role");
    },
  });

  const handleInvite = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const submittedEmail = inviteEmail.trim();
    if (!submittedEmail || !emailSchema.safeParse(submittedEmail).success)
      return;

    inviteMutation.mutate(
      {
        request: {
          sendInviteRequestBody: {
            email: submittedEmail,
            roleId: effectiveInviteRoleId,
          },
        },
      },
      {
        onSuccess: () => {
          void (async () => {
            await invalidateTeamData();
            toast.success(`Invite sent to ${submittedEmail}`);
            setInviteEmail("");
            setInviteEmailTouched(false);
            setInviteRoleId(undefined);
            setIsInviteDialogOpen(false);
          })();
        },
      },
    );
  };

  const handleRemoveMember = () => {
    if (!memberToRemove || memberToRemove.email === user.email) return;

    const displayName = memberToRemove.name ?? memberToRemove.email;
    removeMemberMutation.mutate(
      {
        request: {
          userId: memberToRemove.userId,
        },
      },
      {
        onSuccess: () => {
          void (async () => {
            await invalidateAfterRemoval();
            toast.success(`${displayName} has been removed`);
            setMemberToRemove(null);
          })();
        },
      },
    );
  };

  const handleRevokeInvite = () => {
    if (!inviteToCancel) return;

    const email = inviteToCancel.email;
    revokeInviteMutation.mutate(
      {
        request: {
          invitationId: inviteToCancel.id,
        },
      },
      {
        onSuccess: () => {
          void (async () => {
            await invalidateTeamData();
            toast.success(`Invite to ${email} has been cancelled`);
            setInviteToCancel(null);
          })();
        },
      },
    );
  };

  const handleUpdateInviteRole = (
    invite: OrganizationInvitation,
    roleId: string,
  ) => {
    if (!roleId || roleId === getInviteRoleId(invite)) return;

    updateInviteRoleMutation.mutate(
      {
        request: {
          updateInviteRoleRequestBody: {
            invitationId: invite.id,
            roleId,
          },
        },
      },
      {
        onSuccess: () => {
          void (async () => {
            await invalidateAllListInvites(queryClient);
            toast.success(`Invite role changed to ${getRoleName(roleId)}`);
          })();
        },
      },
    );
  };

  const handleResendInvite = (invite: OrganizationInvitation) => {
    const inviteRoleId = getInviteRoleId(invite) ?? effectiveInviteRoleId;

    // Must revoke first — the unique partial index (org_id, email) WHERE
    // state = 'pending' blocks a second pending invite for the same email.
    // Pass this invite's role ID so the resent invite preserves the role.
    revokeInviteMutation.mutate(
      { request: { invitationId: invite.id } },
      {
        onSuccess: () => {
          inviteMutation.mutate(
            {
              request: {
                sendInviteRequestBody: {
                  email: invite.email,
                  roleId: inviteRoleId,
                },
              },
            },
            {
              onSuccess: () => {
                void (async () => {
                  await invalidateTeamData();
                  toast.success(`Invite resent to ${invite.email}`);
                })();
              },
              onError: () => {
                void (async () => {
                  await invalidateTeamData();
                  toast.error(
                    `Failed to resend invite to ${invite.email}. The previous invite was revoked — please send a new invite.`,
                  );
                })();
              },
            },
          );
        },
        onError: () => {
          toast.error(`Failed to revoke previous invite for ${invite.email}`);
        },
      },
    );
  };

  const membersByUserId = new Map(members.map((m) => [m.userId, m]));

  const memberMenuDeps: MemberMenuDeps = {
    accessMemberByUserId,
    adminCount,
    adminRoleId,
    challengesHref: orgRoutes.access.challenges.href(),
    identityAccessHref: memberAccessHref,
    canUseKillswitch: killswitchAccess.canAccess,
    navigate,
    roleIdsByUserId,
    scimManaged: Boolean(organization.scimEnabled),
    setChangingMember,
    setMemberToRemove,
    userEmail: user.email,
  };

  const memberColumns: Column<OrganizationUser>[] = [
    {
      key: "member",
      header: "Member",
      width: "1fr",
      render: (member) => (
        <Stack direction="horizontal" align="center" gap={3}>
          {member.photoUrl ? (
            <img
              src={member.photoUrl}
              alt={member.name}
              className="h-8 w-8 rounded-full"
            />
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-medium"
              style={getIdentityTint(member.id, isDark)}
            >
              {member.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </div>
          )}
          <Stack direction="vertical" gap={0}>
            <div className="flex flex-wrap items-center gap-2">
              {/* The row click opens the same page, but a handler is not a
                  link: no cmd+click, no middle-click, no copy-link, and nothing
                  a screen reader announces as navigation. Keyed on userId, the
                  same field the row click uses. */}
              <Text variant="body" className="font-medium">
                <IdentityLink identifier={{ userId: member.userId }}>
                  {member.name}
                </IdentityLink>
              </Text>
            </div>
            <Text variant="body" className="text-muted-foreground text-sm">
              {member.email}
            </Text>
          </Stack>
        </Stack>
      ),
    },
    {
      key: "joinedAt",
      header: "Joined",
      width: "200px",
      render: (member) => (
        <Text
          variant="body"
          className="text-muted-foreground whitespace-nowrap"
        >
          <HumanizeDateTime date={member.createdAt} />
        </Text>
      ),
    },
    {
      key: "role",
      header: "Roles",
      width: "200px",
      render: (member) => {
        const accessMember = accessMemberByUserId.get(member.userId);
        const memberRoleIds = accessMember
          ? allMemberRoleIds(accessMember)
          : [];
        if (memberRoleIds.length === 0)
          return <span className="text-muted-foreground">—</span>;
        const MAX_VISIBLE = 1;
        const visible = memberRoleIds.slice(0, MAX_VISIBLE);
        const overflow = memberRoleIds.slice(MAX_VISIBLE);
        return (
          <Stack direction="horizontal" gap={1} className="flex-wrap">
            {visible.map((roleId) => (
              <MemberRoleLink
                key={roleId}
                href={`${orgRoutes.access.roles.href()}?editRole=${roleId}`}
                name={getRoleName(roleId)}
                sourceDescription={
                  accessMember &&
                  describeMemberRoleSource(
                    memberRoleSource(accessMember, roleId),
                  )
                }
              />
            ))}
            {overflow.length > 0 && (
              <SimpleTooltip
                tooltip={overflow
                  .map((id) =>
                    overflowRoleLabel(
                      getRoleName(id),
                      accessMember &&
                        shortMemberRoleSource(
                          memberRoleSource(accessMember, id),
                        ),
                    ),
                  )
                  .join(", ")}
              >
                <span className="text-muted-foreground cursor-pointer border px-1.5 py-0.5 text-xs">
                  +{overflow.length} more
                </span>
              </SimpleTooltip>
            )}
          </Stack>
        );
      },
    } satisfies Column<OrganizationUser>,
    {
      key: "lastLogin",
      header: "Last active",
      width: "200px",
      render: (member) => (
        <Text
          variant="body"
          className="text-muted-foreground whitespace-nowrap"
        >
          {member.lastLogin ? (
            <HumanizeDateTime date={member.lastLogin} />
          ) : (
            <span className="text-muted-foreground/50">—</span>
          )}
        </Text>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "60px",
      render: (member) => {
        const model = getMemberMenuModel(member, memberMenuDeps);

        return (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Actions for ${member.name}`}
                className={cn(
                  "text-muted-foreground hover:bg-accent hover:text-foreground flex h-8 w-8 cursor-pointer items-center justify-center transition-colors",
                )}
              >
                <Ellipsis className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {identityHref({ userId: member.userId }) && (
                <DropdownMenuItem asChild>
                  <Link to={identityHref({ userId: member.userId }) ?? ""}>
                    View profile
                  </Link>
                </DropdownMenuItem>
              )}
              {model.showManageRoles &&
                (model.scimManaged ? (
                  <SimpleTooltip tooltip="Role assignments are managed by your identity provider. Configure them under SSO → SCIM in identity settings.">
                    <span className="inline-flex w-full">
                      <DropdownMenuItem disabled className="w-full">
                        Manage roles
                      </DropdownMenuItem>
                    </span>
                  </SimpleTooltip>
                ) : (
                  model.accessMember && (
                    <RequireScope scope="org:admin" level="component">
                      <DropdownMenuItem onSelect={model.openManageRoles}>
                        Manage roles
                      </DropdownMenuItem>
                    </RequireScope>
                  )
                ))}
              {model.showChallenges && (
                <DropdownMenuItem onSelect={model.openChallenges}>
                  View challenges
                </DropdownMenuItem>
              )}
              {model.showKillswitch && (
                <>
                  <DropdownMenuItem onSelect={model.openViewKillswitches}>
                    View killswitches
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={model.openKillswitch}>
                    New killswitch…
                  </DropdownMenuItem>
                </>
              )}
              {model.canRemove && (
                <>
                  <DropdownMenuSeparator />
                  <RequireScope scope="org:admin" level="component">
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onSelect={model.openRemove}
                    >
                      Remove member
                    </DropdownMenuItem>
                  </RequireScope>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const inviteColumns: Column<OrganizationInvitation>[] = [
    {
      key: "email",
      header: "Email",
      width: "2fr",
      render: (invite) => {
        const isExpired = invite.state === "expired";
        return (
          <Stack
            direction="horizontal"
            align="center"
            gap={3}
            className={isExpired ? "opacity-50" : ""}
          >
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium"
              style={getIdentityTint(invite.email, isDark)}
            >
              {invite.email
                .split("@")[0]
                ?.replace(/[^a-zA-Z]/g, "")
                .slice(0, 2)
                .toUpperCase() || "?"}
            </div>
            <Text variant="body">{invite.email}</Text>
          </Stack>
        );
      },
    },
    {
      key: "role",
      header: "Role",
      width: "180px",
      render: (invite) => {
        const inviteRole = getInviteRole(invite);
        if (roles.length === 0) {
          return (
            <Text variant="body" className="text-muted-foreground">
              {invite.roleSlug ?? "—"}
            </Text>
          );
        }

        return (
          <Select
            value={inviteRole?.id}
            onValueChange={(roleId) => handleUpdateInviteRole(invite, roleId)}
            disabled={updateInviteRoleMutation.isPending}
          >
            <SelectTrigger>
              <SelectValue placeholder={invite.roleSlug ?? "Select role"} />
            </SelectTrigger>
            <SelectContent>
              {roles.map((role) => (
                <SelectItem key={role.id} value={role.id}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      },
    } satisfies Column<OrganizationInvitation>,
    {
      key: "invitedBy",
      header: "Invited by",
      width: "100px",
      render: (invite) => {
        const inviter = invite.inviterUserId
          ? membersByUserId.get(invite.inviterUserId)
          : undefined;
        if (!inviter) return <span className="text-muted-foreground">—</span>;
        return (
          <SimpleTooltip tooltip={inviter.email}>
            <IdentityLink identifier={{ userId: inviter.userId }}>
              {inviter.photoUrl ? (
                <img
                  src={inviter.photoUrl}
                  alt={inviter.name}
                  className="h-7 w-7 rounded-full"
                />
              ) : (
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium"
                  style={getIdentityTint(inviter.id, isDark)}
                >
                  {inviter.name
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              )}
            </IdentityLink>
          </SimpleTooltip>
        );
      },
    },
    {
      key: "createdAt",
      header: "Sent",
      width: "1fr",
      render: (invite) => (
        <Text
          variant="body"
          className={`text-muted-foreground whitespace-nowrap ${invite.state === "expired" ? "opacity-50" : ""}`}
        >
          <HumanizeDateTime date={invite.createdAt} />
        </Text>
      ),
    },
    {
      key: "expiresAt",
      header: "Expires",
      width: "1fr",
      render: (invite) => {
        const isExpired =
          invite.state === "expired" ||
          (invite.expiresAt && invite.expiresAt < new Date());
        return (
          <Text
            variant="body"
            className={isExpired ? "text-destructive" : "text-muted-foreground"}
          >
            {isExpired
              ? "Expired"
              : invite.expiresAt
                ? formatDistanceToNow(invite.expiresAt, { addSuffix: true })
                : "—"}
          </Text>
        );
      },
    },
    {
      key: "actions",
      header: "",
      width: "120px",
      render: (invite) => (
        <RequireScope scope="org:admin" level="component">
          <Stack direction="horizontal" gap={1}>
            <SimpleTooltip tooltip="Resend invite">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => handleResendInvite(invite)}
                disabled={
                  revokeInviteMutation.isPending || inviteMutation.isPending
                }
              >
                <Button.LeftIcon>
                  <RefreshCw className="h-4 w-4" />
                </Button.LeftIcon>
                <Button.Text className="sr-only">Resend invite</Button.Text>
              </Button>
            </SimpleTooltip>
            <SimpleTooltip tooltip="Revoke invite">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => setInviteToCancel(invite)}
                className="hover:text-destructive"
              >
                <Button.LeftIcon>
                  <X className="h-4 w-4" />
                </Button.LeftIcon>
                <Button.Text className="sr-only">Revoke invite</Button.Text>
              </Button>
            </SimpleTooltip>
          </Stack>
        </RequireScope>
      ),
    },
  ];

  const inviteButton = (
    <RequireScope scope="org:admin" level="component">
      {organization.scimEnabled ? (
        <SimpleTooltip tooltip="Managed by your identity provider">
          <span className="inline-flex">
            <Button onClick={() => setIsInviteDialogOpen(true)} disabled>
              <Button.LeftIcon>
                <UserPlus className="h-4 w-4" />
              </Button.LeftIcon>
              <Button.Text>Invite Member</Button.Text>
            </Button>
          </span>
        </SimpleTooltip>
      ) : (
        <Button onClick={() => setIsInviteDialogOpen(true)}>
          <Button.LeftIcon>
            <UserPlus className="h-4 w-4" />
          </Button.LeftIcon>
          <Button.Text>Invite Member</Button.Text>
        </Button>
      )}
    </RequireScope>
  );

  return (
    <>
      <ResourceListPage
        title="Team Members"
        description={`Manage who has access to ${organization.name}`}
        primaryAction={inviteButton}
      >
        <Stack direction="vertical" gap={8}>
          {/* Members Section */}
          <div>
            {organization.scimEnabled && (
              <Alert
                variant="info"
                dismissible={false}
                className="mb-8 text-sm"
              >
                Directory Sync (SCIM) is enabled. Members are provisioned and
                roles assigned from your identity provider, not here.{" "}
                <Link
                  to={orgRoutes.identity.href()}
                  className="underline underline-offset-2"
                >
                  Manage identity settings
                </Link>
              </Alert>
            )}
            {organization.scimEnabled && (
              <DirectoryPeopleSummary className="-mt-4 mb-8 text-sm" />
            )}

            <div className="relative">
              <Icon
                name="search"
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
              />
              <Input
                type="text"
                placeholder="Search members..."
                value={search}
                onChange={(value) => {
                  setSearch(value);
                  setPage(0);
                }}
                className="mb-4 w-full py-2 pl-9 text-sm"
              />
            </div>

            <Table
              columns={memberColumns}
              data={visibleMembers}
              rowKey={(row) => row.userId}
              // The row is where most people first look for someone, so the
              // whole row opens their profile rather than one link inside it.
              onRowClick={(row) => {
                const href = identityHref({ userId: row.userId });
                if (href) void navigate(href);
              }}
              renderRow={(row, rowElement) => (
                <MemberRowContextMenu
                  key={row.userId}
                  member={row}
                  deps={memberMenuDeps}
                >
                  {rowElement}
                </MemberRowContextMenu>
              )}
              className="min-h-fit"
              noResultsMessage={
                <Stack
                  gap={2}
                  className="bg-background -mx-4 -my-6 h-full p-8"
                  align="center"
                  justify="center"
                >
                  <Users className="text-muted-foreground h-12 w-12" />
                  <Text variant="body" className="text-muted-foreground">
                    {search
                      ? "No members matching your search"
                      : "No team members yet"}
                  </Text>
                </Stack>
              }
            />
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t px-4 py-3">
                <Text variant="body" className="text-muted-foreground text-sm">
                  {safePage * MEMBERS_PAGE_SIZE + 1}–
                  {Math.min((safePage + 1) * MEMBERS_PAGE_SIZE, members.length)}{" "}
                  of {members.length}
                </Text>
                <div className="flex items-center gap-1">
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={safePage === 0}
                  >
                    <Button.LeftIcon>
                      <ChevronLeft className="size-4" />
                    </Button.LeftIcon>
                    <Button.Text className="sr-only">Previous page</Button.Text>
                  </Button>
                  <Button
                    variant="tertiary"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={safePage >= totalPages - 1}
                  >
                    <Button.LeftIcon>
                      <ChevronRight className="size-4" />
                    </Button.LeftIcon>
                    <Button.Text className="sr-only">Next page</Button.Text>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Pending Invites Section */}
          {invites.length > 0 && (
            <div>
              <Stack direction="vertical" gap={1} className="mb-4">
                <h3 className="text-eyebrow">Pending Invites</h3>
                <Text variant="body" className="text-muted-foreground">
                  Invitations that haven't been accepted yet
                </Text>
              </Stack>

              <Table
                columns={inviteColumns}
                data={invites}
                rowKey={(row) => row.id}
                className="min-h-fit"
              />
            </div>
          )}
          {/* Identity signpost */}
          <div className="border-border border-t pt-8">
            {organization.scimEnabled ? (
              <div className="border-border bg-muted/30 flex items-start gap-3 border px-4 py-3">
                <FolderSync className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <Text variant="body" className="text-sm font-medium">
                    Directory Sync is enabled
                  </Text>
                  <Text muted small className="mt-0.5">
                    Team membership and role assignments are managed by your
                    identity provider.{" "}
                    <Link
                      to={orgRoutes.identity.href()}
                      className="text-foreground underline underline-offset-4"
                    >
                      Manage identity settings
                    </Link>
                  </Text>
                </div>
              </div>
            ) : (
              <div className="border-border bg-muted/30 flex items-start gap-3 border px-4 py-3">
                <Shield className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <Text variant="body" className="text-sm font-medium">
                    SSO & Directory Sync
                  </Text>
                  <Text muted small className="mt-0.5">
                    Automate member provisioning and enforce identity provider
                    authentication.{" "}
                    <Link
                      to={orgRoutes.identity.href()}
                      className="text-foreground underline underline-offset-4"
                    >
                      Set up SSO & SCIM
                    </Link>
                  </Text>
                </div>
              </div>
            )}
          </div>
        </Stack>
      </ResourceListPage>

      {/* Invite Dialog */}
      <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Invite Team Member</Dialog.Title>
          </Dialog.Header>
          <form className="space-y-4 py-4" onSubmit={handleInvite}>
            <Text variant="body" className="text-muted-foreground">
              Enter the email address of the person you'd like to invite to{" "}
              <span className="text-foreground font-medium">
                {organization.name}
              </span>
              .
            </Text>
            <InputField
              label="Email address"
              name="email"
              type="email"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                if (!inviteEmailTouched) setInviteEmailTouched(true);
              }}
              onBlur={() => setInviteEmailTouched(true)}
              error={inviteEmailError}
              placeholder="colleague@company.com"
              required
              autoFocus
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              data-1p-ignore
              data-lpignore="true"
              data-bwignore
            />
            {roles.length > 0 && (
              <AnyField
                label="Role"
                optionality="hidden"
                render={() => (
                  <Select
                    value={effectiveInviteRoleId}
                    onValueChange={setInviteRoleId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            )}
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsInviteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  inviteMutation.isPending ||
                  !inviteEmail.trim() ||
                  !!inviteEmailError
                }
              >
                {inviteMutation.isPending ? "Sending..." : "Send Invite"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog>

      {/* Remove Member Dialog */}
      <Dialog
        open={!!memberToRemove}
        onOpenChange={(open) => {
          void (!open && setMemberToRemove(null));
        }}
      >
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Remove Team Member</Dialog.Title>
          </Dialog.Header>
          <div className="space-y-4 py-4">
            <Text variant="body">
              Are you sure you want to remove{" "}
              <span className="font-bold">{memberToRemove?.name}</span> from{" "}
              {organization.name}? They will lose access to all projects and
              resources.
            </Text>
            <div className="flex justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={() => setMemberToRemove(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive-primary"
                onClick={handleRemoveMember}
                disabled={removeMemberMutation.isPending}
              >
                {removeMemberMutation.isPending
                  ? "Removing..."
                  : "Remove Member"}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>

      {/* Cancel Invite Dialog */}
      <Dialog
        open={!!inviteToCancel}
        onOpenChange={(open) => {
          void (!open && setInviteToCancel(null));
        }}
      >
        <Dialog.Content>
          <Dialog.Header>
            <Dialog.Title>Cancel Invite</Dialog.Title>
          </Dialog.Header>
          <div className="space-y-4 py-4">
            <Text variant="body">
              Are you sure you want to cancel the invite to{" "}
              <span className="font-bold">{inviteToCancel?.email}</span>?
            </Text>
            <div className="flex justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={() => setInviteToCancel(null)}
              >
                Keep Invite
              </Button>
              <Button
                variant="destructive-primary"
                onClick={handleRevokeInvite}
                disabled={revokeInviteMutation.isPending}
              >
                {revokeInviteMutation.isPending
                  ? "Revoking..."
                  : "Revoke Invite"}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>

      {/* Change Role Dialog — hidden when directory sync manages role assignment */}
      {!organization.scimEnabled && (
        <ChangeRoleDialog
          member={changingMember}
          onOpenChange={(open) => {
            if (!open) setChangingMember(null);
          }}
        />
      )}
    </>
  );
}
